import { Box, Stack, Typography } from "@mui/material";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import {
  dropzoneFont,
  dropzoneFormatsText,
  dropzoneRef,
} from "./CustomDropzone.tokens";

interface CustomProps {
  title: string;
  formatsText?: string;
}

/**
 * The shared dropzone contents: tinted circle, bold prompt, then the
 * "browse files · formats · size" line. Pass to `CustomDropzone`'s `innerBody`
 * together with `dropzoneSurfaceSx` so the surface and the body match.
 */
const DropzoneBody = ({ title, formatsText }: CustomProps) => (
  <Stack
    alignItems="center"
    spacing={{ xs: 0.625, md: 0.75 }}
    sx={{ padding: { xs: "22px 16px", sm: "30px 22px", md: "38px 24px" } }}
  >
    <Box
      sx={{
        width: { xs: 44, sm: 52, md: 56 },
        height: { xs: 44, sm: 52, md: 56 },
        borderRadius: "50%",
        backgroundColor: dropzoneRef.accentTint,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mb: { xs: 0.625, md: 1 },
      }}
    >
      <FileUploadOutlinedIcon
        sx={{ fontSize: { xs: 20, sm: 23, md: 24 }, color: dropzoneRef.accent }}
      />
    </Box>
    <Typography
      sx={{
        fontSize: dropzoneFont.title,
        fontWeight: 800,
        color: dropzoneRef.heading,
        textAlign: "center",
      }}
    >
      {title}
    </Typography>
    <Typography
      sx={{
        fontSize: dropzoneFont.sub,
        color: dropzoneRef.metaText,
        textAlign: "center",
      }}
    >
      or{" "}
      <Box
        component="span"
        sx={{ color: dropzoneRef.accentText, fontWeight: 700 }}
      >
        browse files
      </Box>{" "}
      · {formatsText ?? dropzoneFormatsText}
    </Typography>
  </Stack>
);

/**
 * Surface styling for the same look. `error` paints the invalid border used
 * when a step blocks on "no document uploaded"; it wins over the resting
 * border but still yields to the component's own drag-over feedback.
 */
export const dropzoneSurfaceSx = ({
  disabled = false,
  error = false,
}: {
  disabled?: boolean;
  error?: boolean;
} = {}) => ({
  // The 2px dashed stroke itself comes from `customDropzoneStyles.dropzone`,
  // which is always spread first — only the colour changes per state.
  cursor: disabled ? "not-allowed" : "pointer",
  borderColor: error ? "error.main" : dropzoneRef.border,
  borderRadius: "16px",
  backgroundColor: "background.paper",
  boxShadow: "none",
  "&:hover": {
    borderColor: error ? "error.main" : dropzoneRef.accent,
    backgroundColor: dropzoneRef.hoverBg,
  },
});

export default DropzoneBody;
